import {
  Alert,
  Box,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import { loginWithEmail, microsoftLogin } from "../../actions/auth.js";
import { useEffect } from "react";

import Captcha from "../../components/captcha.jsx";
import AuthLayout, { authButtonClass } from "../../components/AuthLayout.jsx";
import { homePathFor } from "../../config/roles.js";
import useSnackbar from "../../hooks/useSnackbar.js";

export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn, err, data: userData } = useSelector((state) => state.user);
  const roles = userData?.roles;
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const captchaRef = useRef();
  const [errorMsg, setErrorMsg] = useState("");
  const snackbar = useSnackbar();

  useEffect(() => {
    setLoading(false);
  }, [isLoggedIn, err]);

  const [data, setData] = useState({ email: "", password: "" });
  const handleInputChange = (e) => {
    setData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const newdata = await captchaRef.current.checkAnswer();
    if (newdata[0] === true) {
      setErrorMsg("");
      setLoading(true);

      dispatch(loginWithEmail(data)).then((res) => {
        setLoading(false);
        captchaRef.current?.numRefresh();
        // a successful login resolves with nothing (and the form is already gone)
        if (res?.error) snackbar.showError(res.error);
      });
    } else if (newdata[1] === "") {
      setErrorMsg("Enter the captcha.");
    } else {
      setErrorMsg("Captcha Mismatch");
    }
  };
  // eslint-disable-next-line no-unused-vars
  const handleMicrosoftLogin = () => {
    dispatch(microsoftLogin());
  };

  return isLoggedIn ? (
    <Navigate to={homePathFor(roles)} replace="true" />
  ) : (
    <AuthLayout
      title="Welcome back"
      subtitle="Sign in to continue to your ODR workspace"
      icon={<LockOutlinedIcon />}
    >
      <Box component="form" onSubmit={handleLoginSubmit} display="flex" flexDirection="column" gap={2.2}>
        {Boolean(err) ? <Alert severity="error">{err}</Alert> : null}

        <TextField
          value={data.email}
          type="email"
          label="Email"
          onChange={handleInputChange}
          name="email"
          autoComplete="username"
          required
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MailOutlineIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          value={data.password}
          type={showPassword ? "text" : "password"}
          label="Password"
          onChange={handleInputChange}
          name="password"
          required
          autoComplete="current-password"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockOutlinedIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box display="flex" justifyContent="flex-end" mt={-1}>
          <Link
            component="button"
            type="button"
            underline="hover"
            fontSize={13}
            fontWeight={600}
            onClick={() => navigate("/forget-password", { replace: true })}
          >
            Forgot password?
          </Link>
        </Box>

        <Captcha errorMsg={errorMsg} ref={captchaRef} />

        <Button type="submit" variant="contained" fullWidth size="large" disabled={loading} className={authButtonClass}>
          {loading ? <CircularProgress size={24} className="!text-white" /> : "Sign in"}
        </Button>
      </Box>
    </AuthLayout>
  );
}
